import { Label } from '@radix-ui/react-label'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import _ from 'lodash'
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Ellipsis,
  GraduationCap,
  List,
} from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/elements/dropdown-menu'
import { Skeleton } from '@/components/elements/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/elements/tabs'
import { ToggleGroup, ToggleGroupItem } from '@/components/elements/toggle-group'
import { Layout } from '@/components/Layout'
import { MultiSelect } from '@/components/MultiSelect'
import { getAllEntityCertificates } from '@/services/entity_certificates.service'
import { getStudentEntityCertificates } from '@/services/entity_student_certificates.service'
import { getEvents } from '@/services/events.service'
import { UserStoreContext } from '@/stores/UserStore'
import { formatTimeRange } from '@/utils/formatters'
import { cn } from '@/utils/helpers'

import EventSlideoutEdit from './EventSlideoutEdit'
import EventSlideoutInstructorView from './EventSlideoutInstructorView'
import EventSlideoutView from './EventSlideoutView'

dayjs.extend(customParseFormat)

// Use a sub component so the layout doesn't re-render when the calendar state changes
function Calendar() {
  return (
    <Layout breadcrumbs={['Calendar']}>
      <Layout.Heading title="Calendar" />
      <CalendarView />
    </Layout>
  )
}
export default Calendar

const CalendarView = observer(() => {
  // Context
  const {
    isEntityOwner,
    isEntityAdmin,
    memberRoleForActiveEntity,
    isEntityInstructor,
    isXverseAdmin,
    canManageEntity,
  } = useContext(UserStoreContext)
  // While this is identical to canManageEntity, keep them separate for clarity
  const canManageEvent = isEntityOwner || isEntityAdmin || isEntityInstructor || isXverseAdmin

  const isStudentAndNonStudent = canManageEntity && !!memberRoleForActiveEntity
  const { certificate: stateCertificate } = useLocation()?.state ?? {}

  const { entityId: activeEntity } = useParams()

  const DEFAULT_FILTERS = {
    date: null,
    entity: activeEntity,
    certificate: stateCertificate ? [stateCertificate] : [],
  }
  const today = dayjs()

  // State
  const [selectedDay, setSelectedDay] = useState()
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf('month'))
  // Slideout state
  const [showEventSlideout, setShowEventSlideout] = useState(false)
  const [editEvent, setEditEvent] = useState(null)
  const [showViewSlideout, setShowViewSlideout] = useState(false)
  const [viewEvent, setViewEvent] = useState(null)
  const [showInstructorViewSlideout, setShowInstructorViewSlideout] = useState(false)

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [viewerRole, setViewerRole] = useState(canManageEntity ? 'instructor' : 'student')
  const [selectedView, setSelectedView] = useState('calendar')
  const [listTimeScope, setListTimeScope] = useState('upcoming')

  // Data Loading
  const queryClient = useQueryClient()
  const { data: events, isPending } = useQuery({
    queryKey: ['events', filters],
    queryFn: () => getEvents(filters),
    enabled: selectedView === 'calendar',
  })

  // List view query — uses date-relative filters instead of month-scoped
  const listFilters = {
    entity: activeEntity,
    certificate: filters.certificate,
    date:
      listTimeScope === 'upcoming'
        ? { after: dayjs().format('YYYY-MM-DD') }
        : { before: dayjs().format('YYYY-MM-DD') },
  }
  const { data: listEvents, isPending: listPending } = useQuery({
    queryKey: ['events', 'list', listFilters],
    queryFn: () => getEvents(listFilters),
    enabled: selectedView === 'list',
  })
  const { data: certificates } = useQuery({
    queryKey: ['accounts', 'certificates', activeEntity],
    queryFn: () => getAllEntityCertificates(activeEntity),
    enabled: viewerRole === 'instructor',
  })

  const { data: studentCertificates } = useQuery({
    queryKey: [
      'accounts',
      'students',
      'certificates',
      activeEntity,
      memberRoleForActiveEntity?.id,
    ],
    queryFn: () =>
      getStudentEntityCertificates({
        entityId: activeEntity,
        studentId: memberRoleForActiveEntity?.id,
      }),
    enabled: viewerRole === 'student',
  })

  const isRegisteredForEvent = useCallback(
    (event) =>
      Boolean(
        event?.studentCertificates?.some(
          (cert) => cert.student.id === memberRoleForActiveEntity?.id,
        ),
      ),
    [memberRoleForActiveEntity?.id],
  )

  // Data Processing
  const certificatesForFilter = useMemo(() => {
    if (viewerRole === 'instructor')
      return (
        certificates?.map((certificate) => ({
          label: certificate.name,
          id: certificate.id,
        })) || []
      )
    return (
      studentCertificates?.results.map(({ certificate }) => ({
        label: certificate.name,
        id: certificate.id,
      })) || []
    )
  }, [certificates, studentCertificates, viewerRole])

  const eventsWithRegistration = useMemo(() => {
    if (!events?.results) return []
    return events.results.map((event) => ({
      ...event,
      isRegistered: isRegisteredForEvent(event),
    }))
  }, [events?.results, isRegisteredForEvent])

  const listEventsProcessed = useMemo(() => {
    if (!listEvents?.results) return []
    const processed = listEvents.results.map((event) => ({
      ...event,
      isRegistered: isRegisteredForEvent(event),
    }))
    // Past events: reverse to show most recent first (API returns ascending)
    if (listTimeScope === 'past') return processed.reverse()
    return processed
  }, [listEvents?.results, isRegisteredForEvent, listTimeScope])

  // For students, When studentCertificates is loaded, setFilters to only those certs
  useEffect(() => {
    if (viewerRole !== 'student' || !studentCertificates) return
    setFilters((previous) => {
      if (previous.certificate.length > 0) return previous
      return {
        ...previous,
        certificate: studentCertificates?.results.map((sc) => sc.certificate.id),
      }
    })
  }, [studentCertificates, viewerRole])

  // Calendar Generation
  const daysToDisplay = useMemo(() => {
    const monthStart = currentMonth.startOf('month')
    const monthEnd = currentMonth.endOf('month')

    // For Monday-first calendar: Sunday(0)->6, Monday(1)->0, Tuesday(2)->1, etc.
    const numDaysFromPrevMonth = (monthStart.day() + 6) % 7

    // 42 days in total for 6 weeks of calendar displayed
    const numDaysFromNextMonth = 42 - (monthStart.daysInMonth() + numDaysFromPrevMonth)

    const updatedDays = []

    // Fill in the previous month's days
    _.forEach(_.rangeRight(numDaysFromPrevMonth, 0, -1), (i) => {
      const date = monthStart.subtract(i, 'day').format('YYYY-MM-DD')
      updatedDays.unshift({
        date,
      })
    })

    // Fill in the current month's days
    _.forEach(_.range(monthStart.date(), monthEnd.date() + 1), (i) => {
      const date = monthStart.date(i).format('YYYY-MM-DD')
      updatedDays.push({
        date,
      })
    })

    // Fill in the next month's days
    _.forEach(_.range(1, numDaysFromNextMonth + 1), (i) => {
      const date = monthEnd.add(i, 'day').format('YYYY-MM-DD')
      updatedDays.push({
        date,
      })
    })

    return updatedDays.map((day) => ({
      ...day,
      isCurrentMonth: dayjs(day.date).isSame(currentMonth, 'month'),
      isToday: dayjs(day.date).isSame(today, 'day'),
      events: eventsWithRegistration?.filter((event) =>
        dayjs(event.date, 'YYYY-MM-DD').isSame(day.date, 'day'),
      ),
    }))
  }, [currentMonth, eventsWithRegistration, today])

  useEffect(() => {
    // Reset selected day when the current month changes
    setSelectedDay(null)

    // Reset filters when the current month changes
    setFilters((previous) => ({
      ...previous,
      date: {
        after: currentMonth.startOf('month').format('YYYY-MM-DD'),
        before: currentMonth.endOf('month').format('YYYY-MM-DD'),
      },
    }))
  }, [currentMonth])

  useEffect(() => {
    setFilters((previous) => ({
      ...previous,
      entity: activeEntity,
    }))
  }, [activeEntity])

  if (selectedView === 'calendar' && isPending) {
    return (
      <Layout>
        <div className="flex h-full w-full items-center justify-center">
          <Skeleton className="h-[90%] w-[90%]" />
        </div>
      </Layout>
    )
  }

  return (
    <>
      <div className="flex flex-row justify-end gap-2">
        {isStudentAndNonStudent && (
          <Button
            onClick={() => setViewerRole(viewerRole === 'student' ? 'instructor' : 'student')}
            size="sm"
            variant="secondary"
          >
            Switch to {viewerRole === 'student' ? 'Instructor' : 'Student'} View
          </Button>
        )}
        <div className="flex flex-col gap-1">
          <Label className="text-sm" htmlFor="filter-certificate">
            Filter by Module
          </Label>
          <MultiSelect
            id="filter-certificate"
            options={certificatesForFilter}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                certificate: value || [],
              }))
            }}
            value={filters.certificate}
            width="w-full md:w-56"
          />
        </div>
      </div>
      {/* Calendar */}
      <div className="border-input mt-2 overflow-hidden rounded-md border lg:flex lg:h-full lg:flex-col">
        <header className="bg-muted border-input flex items-center justify-between border-b px-6 py-4 lg:flex-none">
          {selectedView === 'calendar' ? (
            <h2>
              <time dateTime={currentMonth.format('YYYY-MM')}>
                {currentMonth.format('MMMM YYYY')}
              </time>
            </h2>
          ) : (
            <h2>Events</h2>
          )}

          <div className="flex items-center gap-2 sm:gap-4">
            {selectedView === 'calendar' && (
              <div className="bg-background relative flex items-center rounded-md shadow-xs md:items-stretch">
                <button
                  type="button"
                  className="border-input md:hover:bg-accent md:hover:text-accent-foreground flex h-7 w-9 items-center justify-center rounded-l-md border-y-[1.5px] border-l-[1.5px] pr-1 focus:relative sm:h-9 sm:w-12 md:w-9 md:pr-0"
                  onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                >
                  <span className="sr-only">Previous month</span>
                  <ChevronLeft className="size-4 sm:size-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="border-input hover:bg-accent hover:text-accent-foreground hidden border-y-[1.5px] px-3.5 text-sm font-semibold focus:relative md:block"
                  onClick={() => setCurrentMonth(today.startOf('month'))}
                >
                  Today
                </button>
                <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
                <button
                  type="button"
                  className="border-input md:hover:bg-accent md:hover:text-accent-foreground flex h-7 w-9 items-center justify-center rounded-r-md border-y-[1.5px] border-r-[1.5px] pl-1 focus:relative sm:h-9 sm:w-12 md:w-9 md:pl-0"
                  onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                >
                  <span className="sr-only">Next month</span>
                  <ChevronRight className="size-4 sm:size-5" aria-hidden="true" />
                </button>
              </div>
            )}

            {selectedView === 'list' && (
              <Tabs value={listTimeScope} onValueChange={setListTimeScope} className="shrink-0">
                <TabsList>
                  <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                  <TabsTrigger value="past">Past</TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            <ToggleGroup
              type="single"
              value={selectedView}
              onValueChange={(v) => v && setSelectedView(v)}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="calendar" aria-label="Calendar view">
                <CalendarDays className="size-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="List view">
                <List className="size-4" />
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="hidden gap-4 md:flex md:items-center">
              {canManageEvent && (
                <Button type="button" onClick={() => setShowEventSlideout(true)}>
                  Add Event
                </Button>
              )}
            </div>
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger className="border-input hover:bg-accent hover:text-accent-foreground bg-background flex flex-row items-center gap-2 rounded-md border-[1.5px] px-2 py-1 text-sm font-semibold shadow-xs outline-none">
                  <span className="sr-only">Open menu</span>
                  <Ellipsis className="size-4" aria-hidden="true" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {canManageEvent && (
                    <DropdownMenuItem onClick={() => setShowEventSlideout(true)}>
                      Add Event
                    </DropdownMenuItem>
                  )}
                  {selectedView === 'calendar' && (
                    <DropdownMenuItem onClick={() => setCurrentMonth(today.startOf('month'))}>
                      Go to Today
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {selectedView === 'calendar' ? (
          <div className="shadow-sm ring-1 ring-black/5 lg:flex lg:flex-auto lg:flex-col">
            <div className="border-input bg-border grid grid-cols-7 gap-px border-b text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
              <div className="bg-background py-2">
                M<span className="sr-only sm:not-sr-only">on</span>
              </div>
              <div className="bg-background py-2">
                T<span className="sr-only sm:not-sr-only">ue</span>
              </div>
              <div className="bg-background py-2">
                W<span className="sr-only sm:not-sr-only">ed</span>
              </div>
              <div className="bg-background py-2">
                T<span className="sr-only sm:not-sr-only">hu</span>
              </div>
              <div className="bg-background py-2">
                F<span className="sr-only sm:not-sr-only">ri</span>
              </div>
              <div className="bg-background py-2">
                S<span className="sr-only sm:not-sr-only">at</span>
              </div>
              <div className="bg-background py-2">
                S<span className="sr-only sm:not-sr-only">un</span>
              </div>
            </div>

            <div className="bg-border flex text-xs/6 lg:flex-auto">
              <div
                className={cn(
                  'hidden w-full lg:grid lg:grid-cols-7 lg:gap-px',
                  `lg:grid-rows-${Math.ceil(daysToDisplay.length / 7)}`,
                )}
              >
                {daysToDisplay.map((day) => {
                  const selected = selectedDay?.isSame(day.date, 'day') || false

                  return (
                    <button
                      key={day.date}
                      className={cn(
                        day.isCurrentMonth ? 'bg-background' : 'bg-muted text-muted-foreground',
                        selected && 'bg-primary/5',
                        'hover:bg-muted flex flex-col justify-between px-3 py-2',
                      )}
                      type="button"
                      onClick={() => setSelectedDay(dayjs(day.date))}
                    >
                      <time
                        dateTime={day.date}
                        className={
                          day.isToday
                            ? 'bg-primary flex size-6 items-center justify-center self-end rounded-full font-semibold text-white'
                            : 'self-end'
                        }
                      >
                        {day.date.split('-').pop().replace(/^0/, '')}
                      </time>
                      <ul className="flex max-w-full list-none flex-col gap-1 text-left">
                        {day.events.map((event) => (
                          <li key={event.id} className="flex items-center gap-1">
                            <span
                              className={cn(
                                'inline-block h-2 w-2 shrink-0 rounded-full',
                                event.isRegistered ? 'bg-success' : 'bg-primary',
                              )}
                            />
                            <span className="leading-none">{event.name}</span>
                          </li>
                        ))}
                      </ul>
                    </button>
                  )
                })}
              </div>
              <div
                className={cn(
                  'isolate grid w-full grid-cols-7 gap-px lg:hidden',
                  `grid-rows-${Math.ceil(daysToDisplay.length / 7)}`,
                )}
              >
                {daysToDisplay.map((day) => {
                  const selected = selectedDay?.isSame(day.date, 'day') || false

                  return (
                    <button
                      key={day.date}
                      className={cn(
                        day.isCurrentMonth ? 'bg-background' : 'bg-muted text-muted-foreground',
                        selected && 'bg-primary/5',
                        'hover:bg-muted relative flex h-14 flex-col p-1 focus:z-10',
                      )}
                      type="button"
                      onClick={() => setSelectedDay(dayjs(day.date))}
                    >
                      <time
                        dateTime={day.date}
                        className={cn(
                          day.isToday && 'bg-primary rounded-full text-white',
                          'ml-auto size-6',
                        )}
                      >
                        {day.date.split('-').pop().replace(/^0/, '')}
                      </time>
                      <ul className="flex max-w-full list-none gap-1 text-left">
                        {day.events.map((event) => (
                          <li key={event.id}>
                            <span
                              className={cn(
                                'inline-block h-2 w-2 rounded-full',
                                event.isRegistered ? 'bg-success' : 'bg-primary',
                              )}
                            />
                          </li>
                        ))}
                      </ul>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="border-input bg-muted flex h-1/3 w-full flex-col overflow-y-auto border-t p-4">
              {selectedDay &&
                daysToDisplay
                  ?.find((day) => dayjs(day.date).isSame(selectedDay, 'day'))
                  ?.events.map((event) =>
                    viewerRole === 'student' ? (
                      <SingleEventStudentBar
                        key={event.id}
                        event={event}
                        memberRoleForActiveEntity={memberRoleForActiveEntity}
                        setViewEvent={setViewEvent}
                        setShowViewSlideout={setShowViewSlideout}
                      />
                    ) : (
                      <SingleEventInstructorBar
                        key={event.id}
                        event={event}
                        canManageEvent={canManageEvent}
                        setEditEvent={setEditEvent}
                        setShowEventSlideout={setShowEventSlideout}
                        setViewEvent={setViewEvent}
                        setShowViewSlideout={setShowInstructorViewSlideout}
                      />
                    ),
                  )}
            </div>
          </div>
        ) : (
          <EventListView
            events={listEventsProcessed}
            isPending={listPending}
            listTimeScope={listTimeScope}
            viewerRole={viewerRole}
            memberRoleForActiveEntity={memberRoleForActiveEntity}
            canManageEvent={canManageEvent}
            setViewEvent={setViewEvent}
            setShowViewSlideout={setShowViewSlideout}
            setEditEvent={setEditEvent}
            setShowEventSlideout={setShowEventSlideout}
            setShowInstructorViewSlideout={setShowInstructorViewSlideout}
          />
        )}
      </div>
      {showEventSlideout && (
        <EventSlideoutEdit
          editEvent={editEvent}
          resetAndClose={() => {
            setShowEventSlideout(false)
            setEditEvent(null)
            queryClient.invalidateQueries({ queryKey: ['events'] })
          }}
        />
      )}
      {showViewSlideout && viewEvent && (
        <EventSlideoutView
          viewEvent={viewEvent}
          resetAndClose={() => {
            setShowViewSlideout(false)
            setViewEvent(null)
            // Probably should only invalidate if something was changed
            queryClient.invalidateQueries({ queryKey: ['events'] })
          }}
        />
      )}
      {showInstructorViewSlideout && viewEvent && (
        <EventSlideoutInstructorView
          viewEvent={viewEvent}
          resetAndClose={() => {
            setShowInstructorViewSlideout(false)
            setViewEvent(null)
            // Probably should only invalidate if something was changed
            queryClient.invalidateQueries({ queryKey: ['events'] })
          }}
        />
      )}
    </>
  )
})

const EventListView = ({
  events,
  isPending: isListPending,
  listTimeScope,
  viewerRole,
  memberRoleForActiveEntity,
  canManageEvent,
  setViewEvent,
  setShowViewSlideout,
  setEditEvent,
  setShowEventSlideout,
  setShowInstructorViewSlideout,
}) => {
  const groupedEvents = useMemo(() => {
    if (!events?.length) return []
    const groups = {}
    events.forEach((event) => {
      const key = dayjs(event.date).format('MMMM YYYY')
      if (!groups[key]) groups[key] = []
      groups[key].push(event)
    })
    return Object.entries(groups)
  }, [events])

  if (isListPending) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }

  if (!events?.length) {
    return (
      <div className="text-muted-foreground flex flex-col items-center justify-center py-12">
        <p>No {listTimeScope === 'upcoming' ? 'upcoming' : 'past'} events</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-4">
      {groupedEvents.map(([month, monthEvents]) => (
        <div key={month}>
          <h3 className="text-muted-foreground mb-2 text-sm font-semibold">{month}</h3>
          <div>
            {monthEvents.map((event) =>
              viewerRole === 'student' ? (
                <SingleEventStudentBar
                  key={event.id}
                  event={event}
                  showDate
                  memberRoleForActiveEntity={memberRoleForActiveEntity}
                  setViewEvent={setViewEvent}
                  setShowViewSlideout={setShowViewSlideout}
                />
              ) : (
                <SingleEventInstructorBar
                  key={event.id}
                  event={event}
                  showDate
                  canManageEvent={canManageEvent}
                  setEditEvent={setEditEvent}
                  setShowEventSlideout={setShowEventSlideout}
                  setViewEvent={setViewEvent}
                  setShowViewSlideout={setShowInstructorViewSlideout}
                />
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

EventListView.propTypes = {
  events: PropTypes.array,
  isPending: PropTypes.bool,
  listTimeScope: PropTypes.string.isRequired,
  viewerRole: PropTypes.string.isRequired,
  memberRoleForActiveEntity: PropTypes.object,
  canManageEvent: PropTypes.bool.isRequired,
  setViewEvent: PropTypes.func.isRequired,
  setShowViewSlideout: PropTypes.func.isRequired,
  setEditEvent: PropTypes.func.isRequired,
  setShowEventSlideout: PropTypes.func.isRequired,
  setShowInstructorViewSlideout: PropTypes.func.isRequired,
}

export const SingleEventStudentBar = ({
  event,
  showDate,
  memberRoleForActiveEntity,
  setViewEvent,
  setShowViewSlideout,
}) => {
  const isRegisteredForEvent = Boolean(
    event?.studentCertificates?.some((cert) => cert.student.id === memberRoleForActiveEntity?.id),
  )

  const registrationButtonLabel = useMemo(() => {
    if (isRegisteredForEvent) return 'View Event Details'
    if (event.registrations < event.capacity) return 'Register'
    return 'Full'
  }, [isRegisteredForEvent, event.capacity, event.registrations])

  return (
    <div
      key={event.id}
      className={cn(
        'bg-background flex flex-row items-center justify-between gap-1 border-x border-b-1 p-2 first:rounded-t-md first:border-t-1 last:rounded-b-md',
      )}
    >
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold">{event.name}</span>

        <div className="flex flex-row gap-1">
          <Clock className="stroke-primary size-4" />
          <time className="text-muted-foreground text-xs">
            {showDate && `${dayjs(event.date).format('ddd, MMM D')} · `}
            {formatTimeRange(event.startTime, event.endTime)}
          </time>
        </div>

        {event.certificates.length > 0 && (
          <div className="text-muted-foreground flex gap-1 text-xs">
            <GraduationCap className="size-4" />{' '}
            {event.certificates.map((cert) => cert.name).join(', ')}
          </div>
        )}
      </div>
      <div className="flex flex-row gap-2">
        {isRegisteredForEvent && (
          <Badge variant="success" className="mr-2">
            Registered
          </Badge>
        )}
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={!isRegisteredForEvent && event.registrations >= event.capacity}
          onClick={() => {
            setViewEvent(event)
            setShowViewSlideout(true)
          }}
        >
          {registrationButtonLabel}
        </Button>
      </div>
    </div>
  )
}

SingleEventStudentBar.propTypes = {
  event: PropTypes.object.isRequired,
  showDate: PropTypes.bool,
  memberRoleForActiveEntity: PropTypes.object,
  setViewEvent: PropTypes.func.isRequired,
  setShowViewSlideout: PropTypes.func.isRequired,
}

export const SingleEventInstructorBar = ({
  event,
  showDate,
  canManageEvent,
  setEditEvent,
  setShowEventSlideout,
  setViewEvent,
  setShowViewSlideout,
}) => (
  <div
    key={event.id}
    className={cn(
      'bg-background flex flex-row items-center justify-between gap-1 border-x border-b-1 p-2 first:rounded-t-md first:border-t-1 last:rounded-b-md',
    )}
  >
    <div className="flex flex-col gap-1">
      <span className="text-sm font-semibold">{event.name}</span>

      <div className="flex flex-row gap-1">
        <Clock className="stroke-primary size-4" />
        <time className="text-muted-foreground text-xs">
          {showDate && `${dayjs(event.date).format('ddd, MMM D')} · `}
          {formatTimeRange(event.startTime, event.endTime)}
        </time>
      </div>

      {event.certificates.length > 0 && (
        <div className="text-muted-foreground flex gap-1 text-xs">
          <GraduationCap className="size-4" />{' '}
          {event.certificates.map((cert) => cert.name).join(', ')}
        </div>
      )}
    </div>
    <div>
      {event.registrations} / {event.capacity} Registered
    </div>

    <div className="flex flex-row gap-2">
      {canManageEvent && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            setEditEvent(event)
            setShowEventSlideout(true)
          }}
        >
          Edit
        </Button>
      )}
      <Button
        type="button"
        variant="default"
        size="sm"
        onClick={() => {
          setViewEvent(event)
          setShowViewSlideout(true)
        }}
      >
        View Event Details
      </Button>
    </div>
  </div>
)

SingleEventInstructorBar.propTypes = {
  event: PropTypes.object.isRequired,
  showDate: PropTypes.bool,
  canManageEvent: PropTypes.bool.isRequired,
  setEditEvent: PropTypes.func.isRequired,
  setShowEventSlideout: PropTypes.func.isRequired,
  setViewEvent: PropTypes.func.isRequired,
  setShowViewSlideout: PropTypes.func.isRequired,
}
